import React from 'react'
import {
  Controller,
  Control,
  UseFormSetValue,
  UseFormGetValues,
} from 'react-hook-form'
import { darken } from '@mui/material/styles'
import {
  Button,
  IconButton,
  Typography,
  TextField,
  Grid,
  Box,
} from '@mui/material'
import { Delete, AccountCircle } from '@mui/icons-material'
import { useDropzone, FileWithPath, FileError } from 'react-dropzone'
import { FilePathWithPreview } from 'types'
import { AgentCreateFormFields } from '../index'
import { useTranslation } from 'react-i18next'
import { EMAIL_REGEX } from 'helpers/constants'
interface PartnerProps {
  nestIndex: number
  control: Control<AgentCreateFormFields>
  setValue: UseFormSetValue<AgentCreateFormFields>
  getValues: UseFormGetValues<AgentCreateFormFields>

  remove: (index?: number | number[] | undefined) => void
}

const Partner = (props: PartnerProps) => {
  const { t } = useTranslation()
  const { control, setValue, getValues, nestIndex, remove } = props

  const [files, setFiles] = React.useState<FilePathWithPreview[]>(() => {
    const photos = getValues(`partners.${nestIndex}`)?.attachments ?? []
    return photos.map((item) => ({ ...item, type: 'attachment' }))
  })

  React.useEffect(() => {
    setValue(`partners.${nestIndex}.attachments`, files)
  }, [files, nestIndex, setValue])

  const onDrop = React.useCallback((acceptedFiles) => {
    if (acceptedFiles?.length) {
      setFiles(
        acceptedFiles.map((file) =>
          Object.assign(file, {
            preview: URL.createObjectURL(file),
          })
        )
      )
    }
  }, [])

  const { acceptedFiles, fileRejections, getRootProps, getInputProps } =
    useDropzone({
      onDrop,
      maxFiles: 1,
      multiple: false,
      maxSize: 2000000,
      accept: 'image/*',
    })

  const removeFile = (file, e) => {
    e.preventDefault()
    e.stopPropagation()
    if (file?.preview) URL.revokeObjectURL(file?.preview)
    setFiles([])
  }

  const acceptedFileItems = files.map((file) => (
    <Box
      component="li"
      key={file.path}
      sx={{
        display: 'flex',
        flexFlow: 'row wrap',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <Box
        component="img"
        src={file?.preview}
        sx={{
          maxWidth: 200,
          height: 'auto',
          maxHeight: 150,
          m: 1,
          cursor: 'pointer',
        }}
        onClick={() => window.open(file?.preview, '_blank')}
      />
      <IconButton onClick={(e) => removeFile(file, e)}>
        <Delete color="error" />
      </IconButton>
    </Box>
  ))

  const fileRejectionItems = fileRejections.map(
    ({ file, errors }: { file: FileWithPath; errors: FileError[] }) => (
      <li key={file.path}>
        {file.path}
        <ul>
          {errors?.map((e) => (
            <>
              {e.code === 'file-too-large' ? (
                <Typography component="li" color="error" key={e.code}>
                  {t('validation.maxFileError')}
                </Typography>
              ) : null}
            </>
          ))}
        </ul>
      </li>
    )
  )

  return (
    <Box component="fieldset" sx={{ borderRadius: 2.5 }}>
      <Box component="legend" sx={{ px: 0.5 }}>
        {`${t('dashboard.pages.createAgent.partners.partner')} ${
          nestIndex + 1
        }`}
      </Box>
      <Grid container spacing={2} justifyContent="center">
        <Grid item xs={12}>
          <Controller
            name={`partners.${nestIndex}.contactName` as const}
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextField
                fullWidth
                variant="outlined"
                label={
                  t('dashboard.pages.createAgent.partners.fields.name') + '*'
                }
                size="small"
                error={!!error}
                helperText={error?.message ?? ''}
                {...field}
              />
            )}
            rules={{
              required: t('validation.fieldRequired') as unknown as string,
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <Controller
            name={`partners.${nestIndex}.description` as const}
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextField
                fullWidth
                multiline
                minRows={3}
                variant="outlined"
                label={t(
                  'dashboard.pages.createAgent.partners.fields.description'
                )}
                size="small"
                error={!!error}
                helperText={error?.message ?? ''}
                {...field}
              />
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <Controller
            name={`partners.${nestIndex}.email` as const}
            control={control}
            rules={{
              required: t('validation.fieldRequired') as unknown as string,
              pattern: {
                value: EMAIL_REGEX,
                message: t('validation.emailPatternError' as string),
              },
            }}
            render={({ field, fieldState: { error } }) => (
              <TextField
                fullWidth
                variant="outlined"
                label={`Email*`}
                size="small"
                error={!!error}
                helperText={error?.message ?? ' '}
                {...field}
              />
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <Controller
            name={`partners.${nestIndex}.mobileNumber` as const}
            control={control}
            rules={{
              required: t('validation.fieldRequired') as unknown as string,
            }}
            render={({ field, fieldState: { error } }) => (
              <TextField
                fullWidth
                variant="outlined"
                label={`${t(
                  'dashboard.pages.createAgent.contactInfo.fields.mobileNumber'
                )}*`}
                size="small"
                error={!!error}
                helperText={error?.message ?? ' '}
                {...field}
              />
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <Controller
            control={control}
            name={`attachments.logo`}
            rules={{ required: false }}
            render={({ field }) => (
              <Box
                {...getRootProps()}
                sx={{
                  border: (theme) => `2px solid ${theme.palette.primary.main}`,
                  boxSizing: 'border-box',
                  borderStyle: 'dashed',
                  borderRadius: 2.5,
                  textAlign: 'center',
                  bgcolor: 'lightgrey',
                  height: '100%',
                  minHeight: 150,
                  maxHeight: 150,
                  display: 'flex',
                  flexFlow: 'column wrap',
                  justifyContent: 'center',
                  alignItems: 'center',
                  position: 'relative',
                  py: 2,
                  background: (theme) =>
                    darken(theme.palette.common.white, 0.1),
                  '&:hover': {
                    background: (theme) =>
                      darken(theme.palette.common.white, 0.2),
                    transition: (theme) =>
                      theme.transitions.create(['background-color']),
                  },
                }}
              >
                <Typography sx={{ whiteSpace: 'pre' }}>
                  {t(
                    'dashboard.pages.createAgent.partners.fields.profilePic.label'
                  )}
                </Typography>
                <Typography variant="subtitle2" sx={{ whiteSpace: 'pre' }}>
                  {t(
                    'dashboard.pages.createAgent.partners.fields.profilePic.helper'
                  )}
                </Typography>
                <AccountCircle />
                <input type="file" hidden {...getInputProps()} />
              </Box>
            )}
          />
        </Grid>
        {acceptedFiles?.length ? (
          <Grid item xs>
            <Box component="ul" sx={{ p: 0 }}>
              {acceptedFileItems}
            </Box>
          </Grid>
        ) : null}
        {fileRejectionItems?.length ? (
          <Grid item xs>
            <Typography variant="h4">Rejected files</Typography>
            <Box component="ul" sx={{ p: 0 }}>
              {fileRejectionItems}
            </Box>
          </Grid>
        ) : null}
        <Grid item container xs={12}>
          <Button
            variant="outlined"
            color="error"
            onClick={() => remove(nestIndex)}
            startIcon={<Delete />}
            style={{ margin: '0 auto' }}
          >
            {t('dashboard.pages.createAgent.partners.removeButton')}
          </Button>
        </Grid>
      </Grid>
    </Box>
  )
}

export default Partner
